import type { RouteLocationNormalized } from "vue-router";

type RouteParams = RouteLocationNormalized["params"];

const readRouteParam = (params: RouteParams, key: string): string | undefined => {
  const value = params[key];
  return typeof value === "string" ? value : undefined;
};

export const getRouteParam = (route: RouteLocationNormalized, key: string): string | undefined =>
  readRouteParam(route.params, key);

export const getRouteParamOrEmpty = (route: RouteLocationNormalized, key: string): string =>
  readRouteParam(route.params, key) ?? "";

export function resolveAreaId(route: RouteLocationNormalized): string | undefined {
  const areaId = getRouteParam(route, "areaId");
  if (areaId) {
    return areaId;
  }
  if (typeof route.query.a === "string") {
    return route.query.a;
  }
  return undefined;
}

export function resolveOrganizationId(route: RouteLocationNormalized): string {
  return getRouteParamOrEmpty(route, "organizationId");
}

export const resetDashboardScrollPosition = () => {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return;
  }
  const rootElement = document.getElementById("dashboard");
  const scrollContainer = rootElement?.querySelector<HTMLElement>("main");
  if (scrollContainer) {
    if (typeof scrollContainer.scrollTo === "function") {
      scrollContainer.scrollTo({ left: 0, top: 0 });
      return;
    }
    scrollContainer.scrollTop = 0;
    scrollContainer.scrollLeft = 0;
    return;
  }
  window.scrollTo({ left: 0, top: 0 });
};

export const queueDashboardScrollReset = () => {
  if (typeof window === "undefined") {
    return;
  }
  if (typeof window.requestAnimationFrame === "function") {
    window.requestAnimationFrame(resetDashboardScrollPosition);
    return;
  }
  window.setTimeout(resetDashboardScrollPosition, 0);
};

export const resolveScrollGroup = (route: RouteLocationNormalized | undefined) => {
  return route?.meta.scrollGroup;
};

export const shouldForceScrollReset = (route: RouteLocationNormalized) => Boolean(route?.meta.resetScroll);

export function extractAccountNsAction(raw: unknown): string | null {
  if (typeof raw === "string") {
    return raw;
  }
  if (Array.isArray(raw)) {
    const first = raw.find(item => typeof item === "string");
    return typeof first === "string" ? first : null;
  }
  return null;
}

export function normalizeRouterBase(base: string): string {
  return base.endsWith("/") ? base.slice(0, -1) : base;
}

export function buildAuthRedirectUrl(base: string, fullPath: string): string {
  const normalizedBase = normalizeRouterBase(base);
  const pathToAppend = fullPath || "/";
  return `${normalizedBase}${pathToAppend}`;
}
